import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Selectors } from '@grandvisionhq/auth';
import { Hooks as Hooks$1 } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { ReturnsDataLayer } from '@grandvisionhq/fun-returns';
import { Spinner, Alert } from '@grandvisionhq/iris-ui';
import { sha256 } from '@grandvisionhq/lib-utils-w3c';
import { useIntl, useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { Hooks } from '@grandvisionhq/stores-v2';
import { clearEvents, makeOrderEvent } from '../../data-layer/utils.js';
import { logOrderEvent } from '../../data-layer/orders.js';
import '@grandvisionhq/analytics';
import { useGetOrderDetails } from '../hooks/use-get-order-details.js';
import OrderDetail from './OrderDetail.js';

const OrderDetailContainer = ({ orderNumber: orderNumber1 , showOrderDate  })=>{
    var ref3, ref1, ref2;
    const [hashedEmail1, setHashedEmail] = React.useState(null);
    const intl = useIntl();
    const { getLabel  } = intl;
    const { isPrescriptionGlassesReorderEnabled , isReturnsEnabled , isOrderWorkflowStatusEnabled  } = useFeatureToggles();
    const routes = useSelector(Selectors.getFormattedRoutes);
    const prescriptionsUrl = routes === null || routes === void 0 ? void 0 : routes.prescriptions;
    const { data: detail , loading , error  } = useGetOrderDetails(orderNumber1);
    React.useEffect(()=>{
        /*
      Hash the customer's email for security before using it in the returns flow.
      This ensures sensitive information is not directly exposed in URLs or APIs.
      Additionally, the email is "normalized" by converting it to lowercase before hashing,
      as the backend performs the same normalization before creating the hash.
    */ const hashEmail = function() {
            var _ref = swcHelpers.asyncToGenerator(function*() {
                var ref;
                if ((detail === null || detail === void 0 ? void 0 : (ref = detail.customerDetails) === null || ref === void 0 ? void 0 : ref.email) && isReturnsEnabled) {
                    const hashedEmail = yield sha256(detail.customerDetails.email.toLowerCase());
                    setHashedEmail(hashedEmail);
                }
            });
            return function hashEmail() {
                return _ref.apply(this, arguments);
            };
        }();
        hashEmail();
    }, [
        detail === null || detail === void 0 ? void 0 : (ref3 = detail.customerDetails) === null || ref3 === void 0 ? void 0 : ref3.email,
        isReturnsEnabled
    ]);
    const [storeDetailsQuery, storeDetailsQueryState] = Hooks.useStoreDetailsWithGlobalStoreId();
    // Retrieve store name based on the external id
    const globalStoreId = (detail === null || detail === void 0 ? void 0 : (ref1 = detail.shippingAddress) === null || ref1 === void 0 ? void 0 : ref1.externalId) || undefined;
    const storeName = (ref2 = storeDetailsQueryState.data) === null || ref2 === void 0 ? void 0 : ref2.name;
    const [isModalOpen, setModalOpen] = React.useState(false);
    const [isCancelReturnsModalOpen, setCancelReturnsModalOpen] = React.useState(false);
    const [orderDetailsTracked, setOrderDetailsTracked] = React.useState(false);
    const { href: returnUrl  } = useLink({
        route: 'returns.select-items',
        params: {
            orderNumber: (detail === null || detail === void 0 ? void 0 : detail.orderNumber) || '',
            email: hashedEmail1 || ''
        }
    }) || {};
    const onCloseStoreDetailModal = ()=>{
        setModalOpen(false);
    };
    const onOpenStoreDetailModal = ()=>{
        setModalOpen(true);
    };
    const onOpenCancelReturnsModal = ()=>{
        if (!detail) {
            return;
        }
        const { orderNumber  } = detail;
        logOrderEvent({
            event_type: 'my_online_orders',
            event: 'return_cancel_order_click',
            order: {
                order_code: orderNumber
            }
        });
        setCancelReturnsModalOpen(true);
    };
    const onCloseCancelReturnsModal = ()=>{
        setCancelReturnsModalOpen(false);
    };
    const onClickReturn = ()=>{
        ReturnsDataLayer.pushReturnsEvent({
            interactionType: 'return_order'
        });
    };
    // Fetch store info
    React.useEffect(()=>{
        if (globalStoreId) {
            storeDetailsQuery(globalStoreId);
        }
    }, [
        globalStoreId,
        storeDetailsQuery
    ]);
    // track order detail view for data analytics
    React.useEffect(()=>{
        if (!detail || orderDetailsTracked) {
            return;
        }
        const { orderNumber , orderDate , paymentStatus , shipmentStatus  } = detail;
        clearEvents();
        logOrderEvent(makeOrderEvent('order details', 'view', orderNumber, orderDate, paymentStatus, shipmentStatus));
        setOrderDetailsTracked(true);
    }, [
        detail,
        orderDetailsTracked
    ]);
    const [addToCart, { loading: isReorderLoading  }] = Hooks$1.useAddToCart();
    const onReorder = React.useCallback((id)=>{
        if (!detail || isReorderLoading) {
            return;
        }
        /*
       * Adding the optional `id` property means a single reorder of that line item,
       * otherwise ALL line items of the order are reordered.
       * If a single line item is ordered, we need explicitly add quantity as 1
       */ let lineItems = [];
        if (id) {
            const lineItem = detail.drafts[id];
            if (!lineItem) {
                console.error(`Line item with id "${id}" not found in drafts.`);
                return;
            }
            lineItems = [
                swcHelpers.objectSpread({}, lineItem, {
                    quantity: 1
                })
            ];
        } else {
            lineItems = Object.values(detail.drafts);
        }
        const onError = (errorCodes)=>errorCodes.forEach((code)=>toast.failed(`cart.${code}`)
            )
        ;
        // undefined is passed because addToCart expects onSuccess that we do not use
        addToCart(lineItems, undefined, onError, (e)=>onError([
                e
            ])
        );
    }, [
        detail,
        isReorderLoading,
        addToCart
    ]);
    const onTraceOrder = React.useCallback(()=>{
        if (!detail) {
            return;
        }
        const { orderNumber , orderDate , paymentStatus , shipmentStatus  } = detail;
        clearEvents();
        logOrderEvent(makeOrderEvent('product list', 'follow order', orderNumber, orderDate, paymentStatus, shipmentStatus));
    }, [
        detail
    ]);
    const onDownloadReceiptClick = ()=>{
        if (!detail) {
            return;
        }
        const { orderNumber  } = detail;
        logOrderEvent({
            event_type: 'my_online_orders',
            event: 'download_receipt_click',
            order: {
                order_code: orderNumber
            }
        });
    };
    const labels = {
        order: intl.getLabel('common.order'),
        contactDetailsTitle: intl.getLabel('checkout.completed.contactDetails.title'),
        orderDetails: intl.getLabel('account.order.detail'),
        shipmentMethodTitle: intl.getLabel('checkout.shipment.method.title'),
        storeInfo: intl.getLabel('account.orders.storeInfo'),
        shipmentAddress: intl.getLabel('checkout.shipment.address'),
        billingAddress: intl.getLabel('checkout.billingAddress'),
        paymentMethod: intl.getLabel('account.subscription.payment.method'),
        products: intl.getLabel('account.order.detail.products'),
        configuration: intl.getLabel('account.order.detail.configuration'),
        prescription: intl.getLabel('account.order.detail.prescription'),
        error: intl.getFormattedLabel('checkout.errors.general.message', {
            parseLink: true
        }),
        makeDeliveryMethodName: (method)=>method ? intl.getLabel(`checkout.shipment.method.${method}`) : '-'
        ,
        subscriptionDetails: intl.getLabel('cart.item.subscription.title'),
        addOn: intl.getLabel('products.addOn.title'),
        insuranceDetails: intl.getLabel('products.insurance.title')
    };
    const isPaid = (detail === null || detail === void 0 ? void 0 : detail.rawPaymentStatus.toLowerCase()) === 'paid';
    if (loading) {
        return /*#__PURE__*/ React.createElement(Spinner, {
            "aria-label": getLabel('common.loadingText')
        });
    }
    if (error || !detail) {
        return /*#__PURE__*/ React.createElement(Alert, {
            variant: "error",
            className: "alert-m-default"
        }, labels.error);
    }
    return /*#__PURE__*/ React.createElement(OrderDetail, {
        labels: labels,
        onCloseStoreDetailModal: onCloseStoreDetailModal,
        onOpenStoreDetailModal: onOpenStoreDetailModal,
        onTraceOrder: onTraceOrder,
        onReorder: onReorder,
        detail: detail,
        lineItems: detail.lineItems,
        isModalOpen: isModalOpen,
        prescriptionsUrl: prescriptionsUrl,
        intl: intl,
        storeName: storeName,
        isPrescriptionGlassesReorderEnabled: isPrescriptionGlassesReorderEnabled,
        isReorderAvailable: detail.isReorderAvailable,
        onOpenCancelReturnsModal: onOpenCancelReturnsModal,
        onCloseCancelReturnsModal: onCloseCancelReturnsModal,
        onDownloadReceiptClick: onDownloadReceiptClick,
        returnUrl: returnUrl,
        onClickReturn: onClickReturn,
        isCancelReturnsModalOpen: isCancelReturnsModalOpen,
        isPaid: isPaid,
        isReturnsEnabled: isReturnsEnabled,
        isOrderWorkflowStatusEnabled: isOrderWorkflowStatusEnabled,
        showOrderDate: showOrderDate
    });
};

export { OrderDetailContainer, OrderDetailContainer as default };
//# sourceMappingURL=OrderDetailContainer.js.map
